// Sfondi stilizzati canvas-painting per ALA Meet 6.1(a).
// Tutti dipinti con primitive 2D, palette ALA navy/oro/sabbia, zero asset esterni.
// Le funzioni accettano un CanvasRenderingContext2D e disegnano su un canvas
// di dimensione (w, h). Riutilizzate sia dal client che dallo script preview.

export type StyleId = 'gradient' | 'office' | 'meeting' | 'library' | 'lakemaggiore' | 'skyline' | 'abstract';

const ALA = {
  navy: '#1b2d52',
  navy2: '#243a6a',
  navyLt: '#3a527a',
  navyDeep: '#0f1a30',
  gold: '#b8842a',
  goldLt: '#d4a052',
  goldPale: '#f0e4cc',
  sand: '#f7f5f0',
  sand2: '#eeebe3',
  sand3: '#e5e0d5',
};

export function paintStyle(id: StyleId, ctx: CanvasRenderingContext2D, w: number, h: number): void {
  switch (id) {
    case 'gradient':     return paintGradient(ctx, w, h);
    case 'office':       return paintOffice(ctx, w, h);
    case 'meeting':      return paintMeeting(ctx, w, h);
    case 'library':      return paintLibrary(ctx, w, h);
    case 'lakemaggiore': return paintLakeMaggiore(ctx, w, h);
    case 'skyline':      return paintSkyline(ctx, w, h);
    case 'abstract':     return paintAbstract(ctx, w, h);
  }
}

// ── 1. Navy & Oro ──
function paintGradient(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, ALA.navy);
  g.addColorStop(0.55, ALA.navy2);
  g.addColorStop(1, ALA.gold);
  ctx.fillStyle = g; ctx.fillRect(0, 0, w, h);
  const r = ctx.createRadialGradient(w * 0.6, h * 0.35, 0, w * 0.6, h * 0.35, Math.max(w, h));
  r.addColorStop(0, 'rgba(255,255,255,0.08)');
  r.addColorStop(1, 'rgba(0,0,0,0.30)');
  ctx.fillStyle = r; ctx.fillRect(0, 0, w, h);
  // sottile pattern diagonale oro
  ctx.strokeStyle = 'rgba(216,160,82,0.06)';
  ctx.lineWidth = 1;
  for (let i = -h; i < w + h; i += 24) {
    ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + h, h); ctx.stroke();
  }
}

// ── 2. Ufficio moderno (stilizzato) ──
function paintOffice(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  // parete (gradient sand)
  const wall = ctx.createLinearGradient(0, 0, 0, h);
  wall.addColorStop(0, '#f5efe2');
  wall.addColorStop(0.7, '#e6dcc6');
  wall.addColorStop(1, '#cdbf9c');
  ctx.fillStyle = wall; ctx.fillRect(0, 0, w, h);

  // pavimento (band warm darker in basso)
  const floorY = h * 0.78;
  const floor = ctx.createLinearGradient(0, floorY, 0, h);
  floor.addColorStop(0, '#9c8762');
  floor.addColorStop(1, '#6e5a3a');
  ctx.fillStyle = floor; ctx.fillRect(0, floorY, w, h - floorY);

  // battiscopa
  ctx.fillStyle = 'rgba(40,25,10,0.35)';
  ctx.fillRect(0, floorY, w, 4);

  // quadro a sinistra con cornice oro + logo ALA discreto
  const fx = w * 0.06, fy = h * 0.12, fw = w * 0.18, fh = h * 0.28;
  ctx.fillStyle = ALA.gold; ctx.fillRect(fx - 4, fy - 4, fw + 8, fh + 8);
  ctx.fillStyle = ALA.navy; ctx.fillRect(fx, fy, fw, fh);
  // mini ALA monogramma dentro
  ctx.fillStyle = ALA.goldLt;
  ctx.font = `800 ${Math.round(fh * 0.36)}px Lato, Arial, sans-serif`;
  ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
  ctx.fillText('ALA', fx + fw / 2, fy + fh / 2);
  ctx.textAlign = 'start'; ctx.textBaseline = 'alphabetic';

  // scrivania in basso (band orizzontale)
  const dY = h * 0.62, dH = h * 0.18;
  const desk = ctx.createLinearGradient(0, dY, 0, dY + dH);
  desk.addColorStop(0, '#5a4530');
  desk.addColorStop(1, '#3a2c1c');
  ctx.fillStyle = desk; ctx.fillRect(0, dY, w, dH);
  // riflesso oro sul bordo superiore della scrivania
  ctx.fillStyle = 'rgba(216,160,82,0.4)';
  ctx.fillRect(0, dY, w, 2);

  // monitor sulla scrivania (centro)
  const mW = w * 0.22, mH = h * 0.20;
  const mX = w * 0.55, mY = dY - mH;
  ctx.fillStyle = '#0a0e16'; ctx.fillRect(mX, mY, mW, mH);
  ctx.fillStyle = '#1a3055'; ctx.fillRect(mX + 4, mY + 4, mW - 8, mH - 8);
  // riflesso del logo ALA sullo schermo
  ctx.fillStyle = 'rgba(216,160,82,0.15)';
  ctx.font = `800 ${Math.round(mH * 0.30)}px Lato, sans-serif`;
  ctx.textAlign = 'center';
  ctx.fillText('ALA', mX + mW / 2, mY + mH * 0.62);
  ctx.textAlign = 'start';
  // stand
  ctx.fillStyle = '#1a1410'; ctx.fillRect(mX + mW / 2 - 8, mY + mH, 16, h * 0.04);
  ctx.fillRect(mX + mW / 2 - 30, mY + mH + h * 0.04, 60, 4);

  // pianta da terra a destra (alta, rigogliosa)
  const pX = w * 0.87;
  // vaso ceramico
  const vTop = h * 0.62, vBot = h * 0.78, vTopW = w * 0.07, vBotW = w * 0.055;
  ctx.fillStyle = '#d8c8a8';
  ctx.beginPath();
  ctx.moveTo(pX - vTopW / 2, vTop);
  ctx.lineTo(pX + vTopW / 2, vTop);
  ctx.lineTo(pX + vBotW / 2, vBot);
  ctx.lineTo(pX - vBotW / 2, vBot);
  ctx.closePath(); ctx.fill();
  // banda decorativa sul vaso
  ctx.fillStyle = ALA.gold;
  ctx.fillRect(pX - vTopW / 2 + 4, vTop + 6, vTopW - 8, 2);
  // ombra interna vaso
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  ctx.fillRect(pX - vTopW / 2 + 2, vTop, vTopW - 4, 4);
  // tronco/stelo
  ctx.strokeStyle = '#3d5a2a'; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.moveTo(pX, vTop - 2); ctx.lineTo(pX, h * 0.30); ctx.stroke();
  // chioma: cluster di foglie a varie dimensioni
  const leaves = [
    { x: pX,         y: h * 0.32, rx: 30, ry: 50, color: '#3d6a3d', rot: -0.3 },
    { x: pX - 22,    y: h * 0.36, rx: 22, ry: 42, color: '#2d5530', rot: -0.6 },
    { x: pX + 22,    y: h * 0.34, rx: 24, ry: 44, color: '#4a7a4a', rot:  0.4 },
    { x: pX - 35,    y: h * 0.42, rx: 18, ry: 36, color: '#2d5530', rot: -0.9 },
    { x: pX + 32,    y: h * 0.42, rx: 20, ry: 38, color: '#4a7a4a', rot:  0.7 },
    { x: pX,         y: h * 0.46, rx: 22, ry: 38, color: '#3d6a3d', rot:  0.1 },
    { x: pX - 16,    y: h * 0.50, rx: 16, ry: 30, color: '#5a8a5a', rot: -0.3 },
    { x: pX + 18,    y: h * 0.52, rx: 16, ry: 30, color: '#3d6a3d', rot:  0.5 },
    { x: pX,         y: h * 0.56, rx: 14, ry: 24, color: '#2d5530', rot:  0    },
  ];
  for (const l of leaves) {
    ctx.fillStyle = l.color;
    ctx.beginPath(); ctx.ellipse(l.x, l.y, l.rx, l.ry, l.rot, 0, Math.PI * 2); ctx.fill();
    // riflesso luce
    ctx.fillStyle = 'rgba(255,255,255,0.10)';
    ctx.beginPath(); ctx.ellipse(l.x - 4, l.y - 6, l.rx * 0.4, l.ry * 0.3, l.rot, 0, Math.PI * 2); ctx.fill();
  }

  // lampada da scrivania (a sinistra del monitor)
  const lX = w * 0.40, lY = dY;
  ctx.fillStyle = '#1a1410';
  ctx.fillRect(lX - 1.5, lY - h * 0.10, 3, h * 0.10); // stelo
  ctx.beginPath();
  ctx.moveTo(lX - 12, lY - h * 0.14);
  ctx.lineTo(lX + 12, lY - h * 0.14);
  ctx.lineTo(lX + 16, lY - h * 0.10);
  ctx.lineTo(lX - 16, lY - h * 0.10);
  ctx.closePath();
  ctx.fillStyle = ALA.gold; ctx.fill();
  // alone luce
  const ll = ctx.createRadialGradient(lX, lY - h * 0.08, 0, lX, lY - h * 0.08, h * 0.18);
  ll.addColorStop(0, 'rgba(255,220,150,0.30)');
  ll.addColorStop(1, 'rgba(255,220,150,0)');
  ctx.fillStyle = ll; ctx.fillRect(0, 0, w, h);
}

// ── 3. Sala riunioni stilizzata (vista prospettica frontale) ──
function paintMeeting(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  // soffitto + parete laterale destra (gradient)
  const wall = ctx.createLinearGradient(0, 0, 0, h);
  wall.addColorStop(0, '#e8e4d8');
  wall.addColorStop(0.55, '#c5beac');
  wall.addColorStop(1, '#7d6e54');
  ctx.fillStyle = wall; ctx.fillRect(0, 0, w, h);

  // pavimento in prospettiva (band scuro in basso)
  const floorY = h * 0.78;
  const floor = ctx.createLinearGradient(0, floorY, 0, h);
  floor.addColorStop(0, '#5a4530');
  floor.addColorStop(1, '#2e2014');
  ctx.fillStyle = floor; ctx.fillRect(0, floorY, w, h - floorY);

  // grande vetrata sul fondo (visibile in prospettiva centrale)
  const gMargX = w * 0.18, gY = h * 0.18, gW = w - gMargX * 2, gH = h * 0.50;
  // sfondo cielo dietro la vetrata
  const sky = ctx.createLinearGradient(gMargX, gY, gMargX, gY + gH);
  sky.addColorStop(0, '#b8cfe6');
  sky.addColorStop(0.6, '#e9e4d2');
  sky.addColorStop(1, '#d4a052');
  ctx.fillStyle = sky; ctx.fillRect(gMargX, gY, gW, gH);
  // sole all'orizzonte (basso nella vetrata)
  const sunCx = gMargX + gW * 0.62, sunCy = gY + gH * 0.78;
  const sunR = ctx.createRadialGradient(sunCx, sunCy, 0, sunCx, sunCy, h * 0.18);
  sunR.addColorStop(0, 'rgba(255,220,140,0.7)');
  sunR.addColorStop(1, 'rgba(255,220,140,0)');
  ctx.fillStyle = sunR; ctx.fillRect(gMargX, gY, gW, gH);
  ctx.fillStyle = '#ffe6a0';
  ctx.beginPath(); ctx.arc(sunCx, sunCy, h * 0.025, 0, Math.PI * 2); ctx.fill();
  // suddivisione vetrata: 3 colonne con cornici oro
  ctx.fillStyle = ALA.gold;
  ctx.fillRect(gMargX - 4, gY - 4, gW + 8, 6); // top
  ctx.fillRect(gMargX - 4, gY + gH, gW + 8, 6); // bottom
  ctx.fillRect(gMargX - 4, gY - 4, 6, gH + 12); // left
  ctx.fillRect(gMargX + gW - 2, gY - 4, 6, gH + 12); // right
  for (let i = 1; i < 3; i++) {
    const x = gMargX + (gW / 3) * i - 2;
    ctx.fillRect(x, gY - 4, 4, gH + 12);
  }
  // linea orizzontale di mezzeria
  ctx.fillRect(gMargX - 4, gY + gH / 2 - 2, gW + 8, 3);

  // tavolo lungo in prospettiva (trapezio che si stringe verso il fondo)
  const tFront = h * 0.86, tBack = h * 0.66;
  const tFrontHalf = w * 0.40, tBackHalf = w * 0.14;
  ctx.fillStyle = '#3a2614';
  ctx.beginPath();
  ctx.moveTo(w / 2 - tFrontHalf, tFront);
  ctx.lineTo(w / 2 + tFrontHalf, tFront);
  ctx.lineTo(w / 2 + tBackHalf, tBack);
  ctx.lineTo(w / 2 - tBackHalf, tBack);
  ctx.closePath(); ctx.fill();
  // top del tavolo con riflesso
  const tTop = ctx.createLinearGradient(0, tBack, 0, tFront);
  tTop.addColorStop(0, '#7a5a3a');
  tTop.addColorStop(1, '#4a3220');
  ctx.fillStyle = tTop;
  ctx.beginPath();
  ctx.moveTo(w / 2 - tFrontHalf + 8, tFront - 4);
  ctx.lineTo(w / 2 + tFrontHalf - 8, tFront - 4);
  ctx.lineTo(w / 2 + tBackHalf - 4, tBack + 2);
  ctx.lineTo(w / 2 - tBackHalf + 4, tBack + 2);
  ctx.closePath(); ctx.fill();
  // riflessi oro sul bordo
  ctx.strokeStyle = ALA.goldLt; ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(w / 2 - tFrontHalf + 8, tFront - 4);
  ctx.lineTo(w / 2 + tFrontHalf - 8, tFront - 4);
  ctx.stroke();

  // sedie ai due lati (visione prospettica: 2 sedie a sinistra + 2 a destra, schienali alti)
  const chairs = [
    { x: w * 0.18, y: tFront + 4, scale: 1.0 },
    { x: w * 0.32, y: tBack + 8, scale: 0.7 },
    { x: w * 0.82, y: tFront + 4, scale: 1.0 },
    { x: w * 0.68, y: tBack + 8, scale: 0.7 },
  ];
  for (const c of chairs) {
    const cw = 40 * c.scale, ch = 60 * c.scale;
    ctx.fillStyle = '#1f2540';
    // schienale
    ctx.beginPath(); ctx.roundRect(c.x - cw / 2, c.y - ch, cw, ch * 0.7, 6); ctx.fill();
    // sedile
    ctx.fillStyle = '#2a3050';
    ctx.beginPath(); ctx.roundRect(c.x - cw / 2 - 2, c.y - ch * 0.35, cw + 4, ch * 0.18, 4); ctx.fill();
    // dettaglio oro sullo schienale
    ctx.fillStyle = ALA.gold;
    ctx.fillRect(c.x - cw / 2 + 3, c.y - ch + 3, cw - 6, 2);
  }
}

// ── 4. Libreria stilizzata ──
function paintLibrary(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  // parete fondo (warm wood gradient)
  const wall = ctx.createLinearGradient(0, 0, 0, h);
  wall.addColorStop(0, '#3a2818');
  wall.addColorStop(1, '#1f140a');
  ctx.fillStyle = wall; ctx.fillRect(0, 0, w, h);

  // 3 ripiani principali con libri grandi e leggibili
  const shelves = 3;
  const margin = w * 0.05;
  const sX = margin, sW = w - margin * 2;
  const topY = h * 0.05;
  const usableH = h * 0.72;       // i ripiani occupano fino al 77% (lascio spazio per la scrivania)
  const shelfH = usableH / shelves;
  const woodTop = '#7b5530';
  const woodEdge = '#502d18';
  const palette = [
    '#7a2c2a', '#a35a2b', '#3b5570', '#2c4a3a', '#5a2c4a',
    '#c89030', '#243a6a', '#7e542a', '#7a5d3a', '#4a2a2a',
    '#6b3030', '#6b8a30', '#3a5260', '#824a30',
  ];

  for (let s = 0; s < shelves; s++) {
    const yBottom = topY + (s + 1) * shelfH;
    // ripiano spesso (legno scuro)
    ctx.fillStyle = woodEdge; ctx.fillRect(sX, yBottom - 4, sW, 14);
    ctx.fillStyle = woodTop; ctx.fillRect(sX, yBottom - 4, sW, 6);

    // libri grandi (8-12 per ripiano)
    let bx = sX + 8;
    const maxBx = sX + sW - 16;
    const minBookH = shelfH * 0.55;
    const maxBookH = shelfH * 0.86;
    let i = 0;
    while (bx < maxBx) {
      // larghezza casuale ampia per leggibilità
      const seed = (bx * 7919 + s * 137 + i * 53) >>> 0;
      const bw = 22 + (seed % 26);                // 22..48 px wide
      const bh = minBookH + ((seed >> 4) % Math.round(maxBookH - minBookH));
      if (bx + bw > maxBx) break;
      const colorIdx = (i + s * 3) % palette.length;
      // corpo libro
      ctx.fillStyle = palette[colorIdx]!;
      ctx.fillRect(bx, yBottom - 4 - bh, bw, bh);
      // ombra sul lato destro (volume)
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.fillRect(bx + bw - 3, yBottom - 4 - bh, 3, bh);
      // strisce decorative oro su circa 1/3 dei libri
      if ((seed % 3) === 0) {
        ctx.fillStyle = 'rgba(216,160,82,0.8)';
        ctx.fillRect(bx + 4, yBottom - 4 - bh * 0.65, bw - 8, 2);
        ctx.fillRect(bx + 4, yBottom - 4 - bh * 0.55, bw - 8, 2);
        // titolo (mini rettangoli)
        ctx.fillStyle = 'rgba(216,160,82,0.5)';
        ctx.fillRect(bx + 6, yBottom - 4 - bh * 0.35, bw - 12, 2);
      }
      // alcuni libri inclinati (appoggiati)
      if ((seed % 9) === 0 && bx + bw + 20 < maxBx) {
        // lascia un piccolo spazio "vuoto"
        bx += bw + 12;
      } else {
        bx += bw + 2;
      }
      i++;
    }
  }

  // scrivania in basso con lampada da lettura
  const dY = topY + usableH + h * 0.02;
  const dH = h - dY;
  const desk = ctx.createLinearGradient(0, dY, 0, h);
  desk.addColorStop(0, '#4a3220');
  desk.addColorStop(1, '#2a1c10');
  ctx.fillStyle = desk; ctx.fillRect(0, dY, w, dH);
  ctx.fillStyle = 'rgba(216,160,82,0.3)';
  ctx.fillRect(0, dY, w, 2);

  // libro aperto sulla scrivania
  ctx.fillStyle = '#e9e0c0';
  ctx.beginPath();
  ctx.moveTo(w * 0.40, dY + dH * 0.4);
  ctx.lineTo(w * 0.60, dY + dH * 0.4);
  ctx.lineTo(w * 0.62, dY + dH * 0.85);
  ctx.lineTo(w * 0.38, dY + dH * 0.85);
  ctx.closePath(); ctx.fill();
  // mezzeria del libro
  ctx.strokeStyle = '#a89878'; ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(w * 0.50, dY + dH * 0.4); ctx.lineTo(w * 0.50, dY + dH * 0.85); ctx.stroke();
  // righe di testo
  ctx.fillStyle = 'rgba(60,40,20,0.4)';
  for (let yi = 0; yi < 5; yi++) {
    ctx.fillRect(w * 0.41, dY + dH * (0.46 + yi * 0.07), w * 0.08, 1.5);
    ctx.fillRect(w * 0.51, dY + dH * (0.46 + yi * 0.07), w * 0.08, 1.5);
  }

  // lampada da lettura warm in alto al centro — alone caldo grande
  const lampX = w * 0.5, lampY = h * 0.35;
  const lamp = ctx.createRadialGradient(lampX, lampY, 0, lampX, lampY, w * 0.45);
  lamp.addColorStop(0, 'rgba(255,210,120,0.30)');
  lamp.addColorStop(0.5, 'rgba(255,210,120,0.10)');
  lamp.addColorStop(1, 'rgba(255,210,120,0)');
  ctx.fillStyle = lamp; ctx.fillRect(0, 0, w, h);

  // vignette laterale leggera
  const side = ctx.createLinearGradient(0, 0, w, 0);
  side.addColorStop(0, 'rgba(0,0,0,0.25)');
  side.addColorStop(0.18, 'rgba(0,0,0,0)');
  side.addColorStop(0.82, 'rgba(0,0,0,0)');
  side.addColorStop(1, 'rgba(0,0,0,0.25)');
  ctx.fillStyle = side; ctx.fillRect(0, 0, w, h);
}

// ── 5. Lago Maggiore stilizzato (omaggio Maccagno) ──
function paintLakeMaggiore(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  // cielo (gradient navy → tone caldo all'orizzonte)
  const sky = ctx.createLinearGradient(0, 0, 0, h * 0.5);
  sky.addColorStop(0, '#1b2d52');
  sky.addColorStop(0.6, '#3a527a');
  sky.addColorStop(1, '#d4a052');
  ctx.fillStyle = sky; ctx.fillRect(0, 0, w, h * 0.5);

  // sole basso sull'orizzonte
  const sunX = w * 0.65, sunY = h * 0.47;
  const sunR = h * 0.06;
  const sunGrad = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, sunR * 3);
  sunGrad.addColorStop(0, 'rgba(255,220,140,0.95)');
  sunGrad.addColorStop(0.3, 'rgba(216,160,82,0.7)');
  sunGrad.addColorStop(1, 'rgba(216,160,82,0)');
  ctx.fillStyle = sunGrad;
  ctx.beginPath(); ctx.arc(sunX, sunY, sunR * 3, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#ffe6a0';
  ctx.beginPath(); ctx.arc(sunX, sunY, sunR, 0, Math.PI * 2); ctx.fill();

  // montagne (3 silhouette overlay)
  const mountains = [
    { color: '#0f1a30', baseY: 0.50, peaks: [[0.0, 0.36], [0.12, 0.30], [0.22, 0.40], [0.38, 0.28], [0.50, 0.36], [0.62, 0.32], [0.78, 0.38], [0.92, 0.30], [1.0, 0.42]] },
    { color: '#1b2d52', baseY: 0.52, peaks: [[0.0, 0.42], [0.10, 0.38], [0.22, 0.44], [0.34, 0.36], [0.48, 0.42], [0.60, 0.38], [0.75, 0.44], [0.88, 0.40], [1.0, 0.46]] },
    { color: '#243a6a', baseY: 0.55, peaks: [[0.0, 0.48], [0.15, 0.46], [0.28, 0.50], [0.42, 0.44], [0.56, 0.48], [0.70, 0.46], [0.84, 0.50], [1.0, 0.48]] },
  ];
  for (const m of mountains) {
    ctx.fillStyle = m.color;
    ctx.beginPath();
    ctx.moveTo(0, h * m.baseY);
    for (const [x, y] of m.peaks) ctx.lineTo(w * x, h * y);
    ctx.lineTo(w, h * m.baseY);
    ctx.closePath(); ctx.fill();
  }

  // lago (gradient deep navy → oro vicino all'orizzonte)
  const lakeGrad = ctx.createLinearGradient(0, h * 0.55, 0, h);
  lakeGrad.addColorStop(0, '#1a2a4a');
  lakeGrad.addColorStop(0.5, '#0f1a30');
  lakeGrad.addColorStop(1, '#0a1124');
  ctx.fillStyle = lakeGrad; ctx.fillRect(0, h * 0.55, w, h * 0.45);

  // riflessi del sole sull'acqua (linee orizzontali oro)
  ctx.strokeStyle = 'rgba(216,160,82,0.6)';
  ctx.lineWidth = 1;
  for (let i = 0; i < 8; i++) {
    const yy = h * 0.57 + i * 14 + (i * i) * 1.5;
    if (yy > h * 0.92) break;
    const len = (w * 0.3) * (1 - i * 0.08);
    ctx.globalAlpha = 0.7 - i * 0.07;
    ctx.beginPath();
    ctx.moveTo(sunX - len / 2, yy);
    ctx.lineTo(sunX + len / 2, yy);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // battello in lontananza (silhouette piccola)
  const bX = w * 0.30, bY = h * 0.62;
  ctx.fillStyle = '#0a1124';
  ctx.beginPath();
  ctx.moveTo(bX - 28, bY);
  ctx.lineTo(bX + 28, bY);
  ctx.lineTo(bX + 22, bY + 6);
  ctx.lineTo(bX - 22, bY + 6);
  ctx.closePath(); ctx.fill();
  // cabina
  ctx.fillRect(bX - 12, bY - 10, 24, 10);
  // antenna
  ctx.strokeStyle = '#0a1124'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(bX, bY - 10); ctx.lineTo(bX, bY - 22); ctx.stroke();
  // luce
  ctx.fillStyle = ALA.goldLt;
  ctx.beginPath(); ctx.arc(bX + 10, bY - 5, 1.5, 0, Math.PI * 2); ctx.fill();

  // costa in primo piano sinistra (silhouette scura curva con qualche albero)
  ctx.fillStyle = '#050a16';
  ctx.beginPath();
  ctx.moveTo(0, h * 0.62);
  ctx.bezierCurveTo(w * 0.05, h * 0.58, w * 0.10, h * 0.66, w * 0.18, h * 0.68);
  ctx.lineTo(w * 0.20, h);
  ctx.lineTo(0, h);
  ctx.closePath(); ctx.fill();
  // alberi
  for (let i = 0; i < 4; i++) {
    const tx = w * (0.04 + i * 0.04);
    const ty = h * (0.66 - i * 0.005);
    ctx.beginPath(); ctx.moveTo(tx, ty);
    ctx.lineTo(tx + 6, ty + 18); ctx.lineTo(tx - 6, ty + 18);
    ctx.closePath(); ctx.fill();
  }
}

// ── 6. Skyline (raffinato) ──
function paintSkyline(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  // cielo
  const sky = ctx.createLinearGradient(0, 0, 0, h * 0.78);
  sky.addColorStop(0, '#0a1124');
  sky.addColorStop(0.5, '#1a2540');
  sky.addColorStop(0.85, '#3a4a6a');
  sky.addColorStop(1, '#5a6a8a');
  ctx.fillStyle = sky; ctx.fillRect(0, 0, w, h * 0.78);

  // luna/luce diffusa
  const moonX = w * 0.18, moonY = h * 0.18;
  const moonGrad = ctx.createRadialGradient(moonX, moonY, 0, moonX, moonY, h * 0.25);
  moonGrad.addColorStop(0, 'rgba(216,160,82,0.30)');
  moonGrad.addColorStop(1, 'rgba(216,160,82,0)');
  ctx.fillStyle = moonGrad; ctx.fillRect(0, 0, w, h * 0.5);
  ctx.fillStyle = 'rgba(232,224,196,0.92)';
  ctx.beginPath(); ctx.arc(moonX, moonY, h * 0.045, 0, Math.PI * 2); ctx.fill();

  // edifici lontani (più chiari, foggy)
  ctx.fillStyle = 'rgba(40,55,90,0.6)';
  const farGround = h * 0.74;
  const farBuildings = [
    [0.05, 0.30], [0.13, 0.22], [0.22, 0.36], [0.31, 0.18], [0.42, 0.28],
    [0.52, 0.20], [0.62, 0.34], [0.72, 0.22], [0.83, 0.30], [0.92, 0.20],
  ];
  for (const [xR, hR] of farBuildings) {
    const bw = w * 0.07, bh = h * hR;
    ctx.fillRect(w * xR, farGround - bh, bw, bh);
  }

  // edifici vicini (più scuri, più dettagliati)
  const groundY = h * 0.78;
  ctx.fillStyle = '#08101c';
  const buildings = [
    [0.00, 0.50], [0.08, 0.36], [0.16, 0.58], [0.24, 0.30], [0.32, 0.46],
    [0.40, 0.28], [0.48, 0.54], [0.56, 0.38], [0.64, 0.48], [0.72, 0.32],
    [0.80, 0.50], [0.88, 0.36], [0.94, 0.44],
  ];
  for (const [xR, hR] of buildings) {
    const bw = w * 0.07, bh = h * hR;
    const bx = w * xR, by = groundY - bh;
    ctx.fillRect(bx, by, bw, bh);
    // finestrelle illuminate sparse (oro)
    ctx.fillStyle = 'rgba(216,160,82,0.85)';
    const stepY = Math.max(10, h * 0.022);
    const stepX = Math.max(8, w * 0.012);
    for (let yy = by + 8; yy < groundY - 4; yy += stepY) {
      for (let xx = bx + 4; xx < bx + bw - 4; xx += stepX) {
        const seed = ((xx * 19) + (yy * 37) + (bx | 0)) & 0xff;
        if (seed % 4 === 0) ctx.fillRect(xx, yy, 2.5, 3);
      }
    }
    ctx.fillStyle = '#08101c';
  }

  // primo piano: lungofiume / strada
  ctx.fillStyle = '#040810';
  ctx.fillRect(0, groundY, w, h - groundY);
  // luci stradali (linea oro discreta)
  ctx.strokeStyle = 'rgba(216,160,82,0.5)';
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(0, groundY + h * 0.02); ctx.lineTo(w, groundY + h * 0.02); ctx.stroke();
}

// ── 7. Pattern astratto navy/oro (zero scena, riconoscibile come brand) ──
function paintAbstract(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  // base gradient diagonale navy → navy-deep
  const base = ctx.createLinearGradient(0, 0, w, h);
  base.addColorStop(0, ALA.navy2);
  base.addColorStop(0.5, ALA.navy);
  base.addColorStop(1, ALA.navyDeep);
  ctx.fillStyle = base; ctx.fillRect(0, 0, w, h);

  // grandi cerchi soft oro (compositing screen-like ottenuto con globalAlpha)
  const blobs = [
    { x: w * 0.20, y: h * 0.25, r: h * 0.55, c: 'rgba(184,132,42,0.18)' },
    { x: w * 0.78, y: h * 0.78, r: h * 0.70, c: 'rgba(212,160,82,0.16)' },
    { x: w * 0.05, y: h * 0.85, r: h * 0.45, c: 'rgba(58,82,122,0.30)' },
    { x: w * 0.92, y: h * 0.15, r: h * 0.35, c: 'rgba(36,58,106,0.40)' },
  ];
  for (const b of blobs) {
    const rg = ctx.createRadialGradient(b.x, b.y, 0, b.x, b.y, b.r);
    rg.addColorStop(0, b.c);
    rg.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = rg; ctx.fillRect(0, 0, w, h);
  }

  // griglia di triangoli sottili oro (low-poly feel)
  ctx.strokeStyle = 'rgba(216,160,82,0.18)';
  ctx.lineWidth = 1;
  const step = Math.max(60, Math.round(h * 0.10));
  for (let y = -step; y < h + step; y += step) {
    for (let x = -step; x < w + step; x += step) {
      const dx = (Math.floor((y / step)) % 2) === 0 ? 0 : step / 2;
      ctx.beginPath();
      ctx.moveTo(x + dx, y);
      ctx.lineTo(x + dx + step, y);
      ctx.lineTo(x + dx + step / 2, y + step * 0.866);
      ctx.closePath();
      ctx.stroke();
    }
  }

  // accent: barra oro diagonale sottile in basso a destra (ALA accent)
  ctx.strokeStyle = ALA.gold;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(w * 0.65, h * 1.05);
  ctx.lineTo(w * 1.05, h * 0.55);
  ctx.stroke();
  ctx.strokeStyle = ALA.goldLt;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(w * 0.70, h * 1.05);
  ctx.lineTo(w * 1.05, h * 0.62);
  ctx.stroke();

  // vignette finale
  const vig = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.3, w / 2, h / 2, Math.max(w, h));
  vig.addColorStop(0, 'rgba(0,0,0,0)');
  vig.addColorStop(1, 'rgba(0,0,0,0.45)');
  ctx.fillStyle = vig; ctx.fillRect(0, 0, w, h);
}

// Esporto le label per coerenza dell'UI
export const STYLE_META: { id: StyleId; label: string; description: string }[] = [
  { id: 'gradient',     label: 'Navy & Oro',       description: 'Sfondo brandizzato ALA, sobrio.' },
  { id: 'abstract',     label: 'Pattern astratto', description: 'Forme geometriche navy/oro, zero scena.' },
  { id: 'office',       label: 'Ufficio moderno',  description: 'Scrivania + quadro col logo, pianta.' },
  { id: 'meeting',      label: 'Sala riunioni',    description: 'Tavolo lungo, vetrata con tramonto.' },
  { id: 'library',      label: 'Libreria',         description: 'Scaffali caldi, libro aperto, lampada.' },
  { id: 'lakemaggiore', label: 'Lago Maggiore',    description: 'Tramonto sul lago, omaggio Maccagno.' },
  { id: 'skyline',      label: 'Skyline',          description: 'Profilo urbano notturno con luna.' },
];
